import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getHomePath } from '../config/navigation';
import { BRAND_NAME, BRAND_LOGO, BRAND_PRODUCT } from '../config/brand';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const schema = yup.object().shape({
    email: yup.string().email('Invalid email').required('Email is required'),
    password: yup.string().required('Password is required'),
});

const Login = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const { register, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(schema),
    });

    const onSubmit = async (data) => {
        setLoading(true);
        const result = await login(data.email, data.password);
        setLoading(false);

        if (result.success) {
            navigate(getHomePath(result.navRole));
        }
    };

    return (
        <div className="min-h-screen bg-surface flex">
            <div className="hidden lg:flex lg:w-[42%] flex-col items-center justify-center bg-brand-900 text-white px-10 py-14 xl:px-14">
                <div className="flex w-full max-w-sm flex-col items-center text-center">
                    <img
                        src={BRAND_LOGO}
                        alt={BRAND_NAME}
                        className="h-14 w-auto max-w-[220px] object-contain"
                    />
                    <h1 className="mt-8 text-3xl font-bold tracking-tight leading-tight">
                        {BRAND_NAME}
                    </h1>
                    <p className="mt-4 text-brand-100 text-base leading-relaxed">
                        {BRAND_PRODUCT} — Learn, apply, document, and build your AI career with real business experience.
                    </p>
                    <p className="mt-10 text-sm text-brand-200">
                        Practical AI · Business · Career development
                    </p>
                </div>
            </div>

            <div className="flex flex-1 items-center justify-center px-4 py-12">
                <div className="w-full max-w-md animate-fade-up">
                    <div className="mb-8 flex justify-center lg:hidden">
                        <img src={BRAND_LOGO} alt={BRAND_NAME} className="h-10 w-auto max-w-[180px] object-contain" />
                    </div>
                    <h2 className="text-2xl font-bold tracking-tight text-ink-950">Sign in</h2>
                    <p className="mt-1.5 text-sm text-ink-600">
                        Access your internship workspace
                    </p>

                    <form className="mt-8 space-y-5" onSubmit={handleSubmit(onSubmit)}>
                        <div>
                            <label className="label" htmlFor="email">Email address</label>
                            <input
                                id="email"
                                {...register('email')}
                                type="email"
                                className="input-field"
                                placeholder="you@example.com"
                                autoComplete="email"
                            />
                            {errors.email && (
                                <p className="mt-1.5 text-sm text-red-700">{errors.email.message}</p>
                            )}
                        </div>

                        <div>
                            <label className="label" htmlFor="password">Password</label>
                            <div className="relative">
                                <input
                                    id="password"
                                    {...register('password')}
                                    type={showPassword ? 'text' : 'password'}
                                    className="input-field pr-10"
                                    placeholder="••••••••"
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-ink-400 hover:text-ink-600 focus:outline-none transition-colors"
                                    tabIndex={-1}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showPassword ? (
                                        <EyeSlashIcon className="h-5 w-5" />
                                    ) : (
                                        <EyeIcon className="h-5 w-5" />
                                    )}
                                </button>
                            </div>
                            {errors.password && (
                                <p className="mt-1.5 text-sm text-red-700">{errors.password.message}</p>
                            )}
                        </div>

                        <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2 text-sm text-ink-700">
                                <input
                                    id="remember-me"
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-border text-brand-700 focus:ring-brand-600"
                                />
                                Remember me
                            </label>
                            <Link to="/forgot-password" className="text-sm font-medium text-brand-700 hover:text-brand-800">
                                Forgot password?
                            </Link>
                        </div>

                        <button type="submit" disabled={loading} className="btn-primary w-full">
                            {loading ? 'Signing in…' : 'Sign in'}
                        </button>
                    </form>

                    <p className="mt-8 text-center text-sm text-ink-600">
                        New intern? Use the onboarding link from your program admin.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;
